'use client';

import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Heart, HelpCircle, Shuffle, Check, Users, X } from 'lucide-react';
import { getCategoriesForGame, Category } from './CategorySelectModal';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import { useSubscription } from '@/hooks/useSubscription';

interface GameSetupModalProps {
  open: boolean;
  onClose: () => void;
  onStart: (players: string[], category: Category, settings: { hintsEnabled: boolean; timeLimitEnabled: boolean; durationMinutes: number }) => void;
  onPremiumRequired: () => void;
  gameName: string;
  gameId: string;
  gameColor: string;
  gameEmoji: string;
}

const PLACEHOLDER_NAMES = [
  'Lukas', 'Sophie', 'Maximilian', 'Hannah', 'Felix',
  'Lena', 'Jonas', 'Emma', 'Tim', 'Laura',
  'Moritz', 'Lea', 'Paul', 'Anna', 'Leon',
];

// Game rules per game ID
const GAME_RULES: Record<string, { title: string; emoji: string; rules: string[] }> = {
  'game-never': {
    title: 'Ich hab noch nie',
    emoji: '🙈',
    rules: [
      'Alle Spieler halten eine Hand hoch.',
      'Reihum sagt jeder: „Ich hab noch nie …" und nennt etwas, das er wirklich noch nie getan hat.',
      'Alle, die es schon getan haben, senken einen Finger.',
      'Wer zuerst alle Finger unten hat, verliert.',
      'Seid ehrlich – das macht das Spiel erst lustig!',
    ],
  },
  'game-truth-dare': {
    title: 'Wahrheit oder Pflicht',
    emoji: '🎯',
    rules: [
      'Reihum wählt jeder Spieler: Wahrheit oder Pflicht.',
      'Bei Wahrheit muss eine ehrliche Antwort auf die Frage gegeben werden.',
      'Bei Pflicht muss die gestellte Aufgabe erfüllt werden.',
      'Wer sich weigert, bekommt eine Strafe (z.B. ein Getränk).',
      'Niemand darf dieselbe Option zweimal hintereinander wählen.',
    ],
  },
  'game-impostor': {
    title: 'Impostor',
    emoji: '👻',
    rules: [
      'Alle Spieler erhalten heimlich dasselbe Wort – außer dem Impostor.',
      'Der Impostor erhält ein ähnliches, aber anderes Wort.',
      'Reihum beschreibt jeder sein Wort mit einem Satz, ohne es zu nennen.',
      'Danach stimmt die Gruppe ab: Wer ist der Impostor?',
      'Der Impostor gewinnt, wenn er nicht enttarnt wird!',
    ],
  },
  'game-antwort-impostor': {
    title: 'Antwort Impostor',
    emoji: '🕵️',
    rules: [
      'Alle Spieler erhalten dieselbe Frage – außer dem Impostor.',
      'Der Impostor erhält eine leicht andere Frage.',
      'Jeder tippt seine Antwort ein, ohne sie zu zeigen.',
      'Alle Antworten werden gleichzeitig aufgedeckt.',
      'Die Gruppe diskutiert und stimmt ab: Wer ist der Impostor?',
      'Der Impostor gewinnt, wenn seine Antwort gut genug passt!',
    ],
  },
  'game-hot-seat': {
    title: 'Hot Seat',
    emoji: '🔥',
    rules: [
      'Ein Spieler setzt sich auf den „heißen Stuhl".',
      'Die anderen Spieler stellen abwechselnd Fragen.',
      'Der Spieler auf dem Hot Seat muss alle Fragen ehrlich beantworten.',
      'Nach einer Runde wechselt der Spieler auf dem Hot Seat.',
      'Keine Frage darf abgelehnt werden – nur die Wahrheit zählt!',
    ],
  },
  'game-emoji': {
    title: 'Emoji Raten',
    emoji: '😎',
    rules: [
      'Ein Spieler wählt ein Wort und erklärt es nur mit Emojis.',
      'Die anderen Spieler raten, was gemeint ist.',
      'Wer zuerst richtig rät, bekommt einen Punkt.',
      'Kein Sprechen, keine Gesten – nur Emojis!',
      'Nach einer festgelegten Anzahl Runden gewinnt der Spieler mit den meisten Punkten.',
    ],
  },
  'game-luegen': {
    title: 'Lügen-Detektiv',
    emoji: '🔍',
    rules: [
      'Jeder Spieler nennt 2 Wahrheiten und 1 Lüge über sich.',
      'Die anderen Spieler diskutieren und raten, welche Aussage die Lüge ist.',
      'Wer die Lüge errät, bekommt einen Punkt.',
      'Der Spieler, dessen Lüge nicht erkannt wird, bekommt auch einen Punkt.',
      'Nach allen Runden gewinnt der Spieler mit den meisten Punkten.',
    ],
  },
  'game-wer-bin-ich': {
    title: 'Wer bin ich?',
    emoji: '🤔',
    rules: [
      'Jeder Spieler bekommt heimlich eine Persona (Promi, Filmfigur etc.) zugeteilt.',
      'Das Handy wird so gehalten, dass alle außer dem Spieler die Persona sehen.',
      'Der Spieler stellt Ja/Nein-Fragen an die Gruppe.',
      'Die Gruppe antwortet nur mit Ja, Nein oder Vielleicht.',
      'Wer seine Persona mit weniger Fragen errät, bekommt mehr Punkte!',
    ],
  },
  default: {
    title: 'Spielregeln',
    emoji: '🎮',
    rules: [
      'Legt die Anzahl der Spieler fest.',
      'Wählt eine Kategorie aus.',
      'Stellt den Timer nach Wunsch ein.',
      'Gebt eure Namen ein.',
      'Viel Spaß beim Spielen!',
    ],
  },
};

const FAVORITES_KEY = 'party_game_favorites';

function getFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
  } catch {
    return [];
  }
}

function toggleFavorite(gameId: string): boolean {
  const favs = getFavorites();
  const idx = favs.indexOf(gameId);
  if (idx >= 0) {
    favs.splice(idx, 1);
  } else {
    favs.push(gameId);
  }
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
  return idx < 0; // returns true if now favorited
}

type Step = 'settings' | 'players';

// Blue color theme
const BG_COLOR = '#1A3A6B';
const BTN_BG = 'rgba(10,30,80,0.7)';

export default function GameSetupModal({
  open,
  onClose,
  onStart,
  onPremiumRequired,
  gameName,
  gameId,
  gameColor,
  gameEmoji
}: GameSetupModalProps) {
  const [step, setStep] = useState<Step>('settings');
  const [playerCount, setPlayerCount] = useState(3);
  const [impostorCount, setImpostorCount] = useState(1);
  const [hintsEnabled, setHintsEnabled] = useState(false);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(true);
  const [durationMinutes, setDurationMinutes] = useState(3);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [playerNames, setPlayerNames] = useState<string[]>([]);
  const [nameError, setNameError] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [showFavToast, setShowFavToast] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const { language } = useLanguage();
  const t = getTranslations(language);
  const gc = (t as any).gameContent;
  const ui = gc?.ui ?? {};
  const gameRulesT = gc?.gameRules ?? {};
  const categoriesT = gc?.categories ?? {};
  const categoryDescT = gc?.categoryDescriptions ?? {};

  const categories = getCategoriesForGame(gameId);

  const { isPremium } = useSubscription();

  useEffect(() => {
    if (open) {
      setIsFavorited(getFavorites().includes(gameId));
    }
  }, [open, gameId]);

  if (!open) return null;

  const handleToggleFavorite = () => {
    const nowFav = toggleFavorite(gameId);
    setIsFavorited(nowFav);
    setShowFavToast(true);
    setTimeout(() => setShowFavToast(false), 2000);
  };

  // Get translated rules
  const rulesData = gameRulesT[gameId] ?? gameRulesT['default'] ?? GAME_RULES[gameId] ?? GAME_RULES['default'];
  const rules = rulesData;

  const handleSettingsNext = () => {
    setPlayerNames(Array.from({ length: playerCount }, () => ''));
    setNameError('');
    setStep('players');
  };

  const handleClose = () => {
    setShowCategoryPicker(false);
    setShowRules(false);
    setStep('settings');
    setPlayerNames([]);
    setNameError('');
    onClose();
  };

  const handleBackToSettings = () => {
    setStep('settings');
    setNameError('');
  };

  const updatePlayerName = (idx: number, value: string) => {
    const updated = [...playerNames];
    updated[idx] = value;
    setPlayerNames(updated);
    setNameError('');
  };

  const fillRandom = () => {
    const shuffled = [...PLACEHOLDER_NAMES].sort(() => Math.random() - 0.5);
    setPlayerNames(shuffled.slice(0, playerCount));
  };

  const handleFinalStart = () => {
    const filled = playerNames.filter((p) => p.trim().length > 0);
    if (filled.length < 2) {
      setNameError(ui.minPlayers ?? 'Bitte mindestens 2 Spielernamen eingeben.');
      return;
    }
    const unique = new Set(filled.map((p) => p.toLowerCase()));
    if (unique.size !== filled.length) {
      setNameError(ui.uniqueNames ?? 'Jeder Spielername muss einzigartig sein.');
      return;
    }
    const cat = selectedCategory || categories.find((c) => !c.premium) || categories[0];
    setStep('settings');
    setPlayerNames([]);
    onStart(filled, cat, { hintsEnabled, timeLimitEnabled, durationMinutes });
  };

  const currentCategoryLabel = selectedCategory ?
    (categoriesT[selectedCategory.id] ?? selectedCategory.label) :
    (categoriesT[categories.find((c) => !c.premium)?.id ?? ''] ?? categories.find((c) => !c.premium)?.label ?? 'Schule');

  const isImpostorGame = gameId === 'game-impostor' || gameId === 'game-antwort-impostor';
  const hasCategories = categories.length > 0;

  // ── Game Rules Overlay ──────────────────────────────────────────────────────
  if (showRules) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center animate-fade-in">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowRules(false)} />
        <div
          className="relative w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden animate-bounce-in"
          style={{ background: '#1C1C2E', maxHeight: '80vh' }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{rules.emoji}</span>
              <h3 className="text-white font-bold text-lg">{rules.title}</h3>
            </div>
            <button
              onClick={() => setShowRules(false)}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
              <X size={18} className="text-white" />
            </button>
          </div>
          <div className="overflow-y-auto p-5 space-y-3" style={{ maxHeight: 'calc(80vh - 72px)' }}>
            <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">{ui.rules ?? 'Spielregeln'}</p>
            {rules.rules.map((rule: string, i: number) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
                  style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED' }}>
                  {i + 1}
                </div>
                <p className="text-white/90 text-sm leading-relaxed pt-1">{rule}</p>
              </div>
            ))}
            <div className="pt-4">
              <button
                onClick={() => setShowRules(false)}
                className="w-full py-3 rounded-2xl font-bold text-white text-base transition-all active:scale-95"
                style={{ background: BG_COLOR }}>
                {ui.understood ?? 'Verstanden!'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Category picker overlay
  if (showCategoryPicker) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCategoryPicker(false)} />
        <div
          className="relative w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden animate-bounce-in"
          style={{ background: '#1a1a2e', maxHeight: '70vh' }}>
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
            <button onClick={() => setShowCategoryPicker(false)} className="p-1.5 rounded-full bg-white/10">
              <ChevronLeft size={18} className="text-white" />
            </button>
            <h3 className="text-white font-bold text-lg">{ui.chooseCategory ?? 'Kategorie wählen'}</h3>
          </div>
          <div className="overflow-y-auto p-4 space-y-2" style={{ maxHeight: 'calc(70vh - 64px)' }}>
            {categories.map((cat) => {
              const isSelected = (selectedCategory?.id ?? categories.find((c) => !c.premium)?.id) === cat.id;
              const catLabel = categoriesT[cat.id] ?? cat.label;
              const catDesc = categoryDescT[cat.id] ?? cat.description;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    if (cat.premium && !isPremium) { onPremiumRequired(); return; }
                    setSelectedCategory(cat);
                    setShowCategoryPicker(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all active:scale-95"
                  style={{
                    background: isSelected ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                    border: isSelected ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent'
                  }}>
                  <span className="text-2xl">{cat.emoji}</span>
                  <div className="flex-1 text-left">
                    <p className="text-white font-semibold text-sm">{catLabel}</p>
                    <p className="text-white/50 text-xs">{catDesc}</p>
                  </div>
                  {isSelected && <span className="text-white font-bold">✓</span>}
                  {cat.premium &&
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'linear-gradient(135deg,#F59E0B,#EF4444)', color: '#fff' }}>PRO</span>
                  }
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Player Name Entry Step ──────────────────────────────────────────────────
  if (step === 'players') {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center animate-fade-in">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
        <div
          className="relative w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden animate-bounce-in flex flex-col"
          style={{ background: BG_COLOR, maxHeight: '95vh' }}>

          {/* Top bar */}
          <div className="flex items-center justify-between px-5 pt-5 pb-2 flex-shrink-0">
            <button
              onClick={handleBackToSettings}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: BTN_BG }}>
              <ChevronLeft size={20} className="text-white" />
            </button>
            <div className="flex items-center gap-2 text-white/70 text-sm font-medium">
              <Users size={14} />
              {ui.playerNames ?? 'Spielernamen'}
            </div>
            <div className="w-10" />
          </div>

          {/* Title */}
          <div className="px-5 pt-3 pb-5 text-center flex-shrink-0">
            <h1
              className="font-black uppercase leading-none"
              style={{
                fontSize: 'clamp(1.8rem, 7vw, 2.8rem)',
                color: '#fff',
                textShadow: '3px 3px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
                letterSpacing: '-0.02em'
              }}>
              {gameName.split(' ').map((word, i) =>
                <span key={i} style={{ color: i % 2 === 1 ? '#F5A623' : '#fff', display: 'inline-block', marginRight: '0.2em' }}>
                  {word}
                </span>
              )}
            </h1>
            <p className="text-white/60 text-sm mt-1">{ui.enterNames ?? 'Gebt eure Namen ein'}</p>
          </div>

          {/* Player name inputs */}
          <div className="overflow-y-auto flex-1 px-4 pb-4">
            <div className="rounded-2xl overflow-hidden mb-3" style={{ background: '#1C1C1E' }}>
              {playerNames.map((name, idx) => (
                <div key={`pname-${idx}`}>
                  {idx > 0 && <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', marginLeft: '16px' }} />}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: 'rgba(245,166,35,0.2)', color: '#F5A623' }}>
                      {idx + 1}
                    </div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => updatePlayerName(idx, e.target.value)}
                      placeholder={`${ui.player ?? 'Spieler'} ${idx + 1}`}
                      maxLength={20}
                      className="flex-1 bg-transparent text-white font-medium text-base outline-none placeholder-white/30"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Random fill button */}
            <button
              onClick={fillRandom}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-white/70 text-sm font-medium transition-all active:scale-95 mb-3"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px dashed rgba(255,255,255,0.2)' }}>
              <Shuffle size={14} />
              {ui.randomNames ?? 'Zufällige Namen'}
            </button>

            {nameError && (
              <p className="text-center text-sm font-medium mb-3" style={{ color: '#FFD700' }}>
                ⚠️ {nameError}
              </p>
            )}
          </div>

          {/* Start Button */}
          <div className="px-4 pb-6 pt-2 flex-shrink-0">
            <button
              onClick={handleFinalStart}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-lg transition-all active:scale-95 hover:opacity-90"
              style={{ background: '#1C1C1E', color: '#fff' }}>
              <Check size={18} />
              {ui.letsGo ?? "Los geht's!"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Settings Step ───────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center animate-fade-in">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div
        className="relative w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden animate-bounce-in flex flex-col"
        style={{ background: BG_COLOR, maxHeight: '95vh' }}>

        {/* Favorite toast */}
        {showFavToast && (
          <div
            className="absolute top-16 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full text-white text-sm font-semibold shadow-lg transition-all"
            style={{ background: 'rgba(0,0,0,0.75)', whiteSpace: 'nowrap' }}>
            {isFavorited ? `❤️ ${ui.addedToFav ?? 'Zu Favoriten hinzugefügt'}` : `🤍 ${ui.removedFromFav ?? 'Aus Favoriten entfernt'}`}
          </div>
        )}

        {/* Top bar */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2 flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
            style={{ background: BTN_BG }}>
            <ChevronLeft size={20} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleFavorite}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: BTN_BG }}>
              <Heart
                size={18}
                className="transition-all"
                style={{ color: isFavorited ? '#FF6B6B' : 'white', fill: isFavorited ? '#FF6B6B' : 'none' }}
              />
            </button>
            <button
              onClick={() => setShowRules(true)}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: BTN_BG }}>
              <HelpCircle size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Game Title */}
        <div className="px-5 pt-4 pb-6 text-center flex-shrink-0">
          <h1
            className="font-black uppercase leading-none"
            style={{
              fontSize: 'clamp(2.2rem, 9vw, 3.5rem)',
              color: '#fff',
              textShadow: '3px 3px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
              letterSpacing: '-0.02em'
            }}>
            {gameName.split(' ').map((word, i) =>
              <span key={i} style={{ color: i % 2 === 1 ? '#F5A623' : '#fff', display: 'inline-block', marginRight: '0.2em' }}>
                {word}
              </span>
            )}
          </h1>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-4 pb-4 space-y-3">
          {/* Settings Card 1 */}
          <div className="rounded-2xl overflow-hidden" style={{ background: '#1C1C1E' }}>
            {/* Players */}
            <div className="flex items-center gap-3 px-4 py-4">
              <span className="text-2xl">☝️</span>
              <span className="flex-1 text-white font-medium text-base">{ui.players ?? 'Spieler'}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPlayerCount(Math.max(2, playerCount - 1))}
                  className="w-7 h-7 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
                  <span className="text-white font-bold text-sm">−</span>
                </button>
                <span className="text-white font-semibold w-5 text-center">{playerCount}</span>
                <button
                  onClick={() => setPlayerCount(Math.min(20, playerCount + 1))}
                  className="w-7 h-7 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
                  <span className="text-white font-bold text-sm">+</span>
                </button>
              </div>
              <ChevronRight size={16} className="text-white/40 ml-1" />
            </div>

            {isImpostorGame &&
              <>
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', marginLeft: '60px' }} />
                {/* Impostor */}
                <div className="flex items-center gap-3 px-4 py-4">
                  <span className="text-2xl">👻</span>
                  <span className="flex-1 text-white font-medium text-base">{ui.impostor ?? 'Impostor'}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setImpostorCount(Math.max(1, impostorCount - 1))}
                      className="w-7 h-7 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
                      <span className="text-white font-bold text-sm">−</span>
                    </button>
                    <span className="text-white font-semibold w-5 text-center">{impostorCount}</span>
                    <button
                      onClick={() => setImpostorCount(Math.min(Math.floor(playerCount / 2), impostorCount + 1))}
                      className="w-7 h-7 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
                      <span className="text-white font-bold text-sm">+</span>
                    </button>
                  </div>
                  <ChevronRight size={16} className="text-white/40 ml-1" />
                </div>

                <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', marginLeft: '60px' }} />
                {/* Hints toggle */}
                <div className="flex items-center gap-3 px-4 py-4">
                  <span className="text-2xl">🔍</span>
                  <span className="flex-1 text-white font-medium text-base">{ui.hintsForImpostor ?? 'Hinweise für Impostor'}</span>
                  <button
                    onClick={() => setHintsEnabled(!hintsEnabled)}
                    className="relative w-12 h-7 rounded-full transition-all duration-300 active:scale-95"
                    style={{ background: hintsEnabled ? '#34C759' : '#3A3A3C' }}>
                    <div
                      className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300"
                      style={{ left: hintsEnabled ? '22px' : '2px' }} />
                  </button>
                </div>
              </>
            }

            {hasCategories &&
              <>
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', marginLeft: '60px' }} />
                {/* Categories */}
                <button
                  onClick={() => setShowCategoryPicker(true)}
                  className="w-full flex items-center gap-3 px-4 py-4 transition-all active:bg-white/5">
                  <span className="text-2xl">💀</span>
                  <span className="flex-1 text-white font-medium text-base text-left">{ui.categories ?? 'Kategorien'}</span>
                  <span className="text-white/50 text-sm mr-1">{currentCategoryLabel}</span>
                  <ChevronRight size={16} className="text-white/40" />
                </button>
              </>
            }
          </div>

          {/* Settings Card 2 */}
          <div className="rounded-2xl overflow-hidden" style={{ background: '#1C1C1E' }}>
            {/* Time limit toggle */}
            <div className="flex items-center gap-3 px-4 py-4">
              <span className="text-2xl">⏰</span>
              <span className="flex-1 text-white font-medium text-base">{ui.timeLimit ?? 'Zeitlimit'}</span>
              <button
                onClick={() => setTimeLimitEnabled(!timeLimitEnabled)}
                className="relative w-12 h-7 rounded-full transition-all duration-300 active:scale-95"
                style={{ background: timeLimitEnabled ? '#34C759' : '#3A3A3C' }}>
                <div
                  className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300"
                  style={{ left: timeLimitEnabled ? '22px' : '2px' }} />
              </button>
            </div>

            {timeLimitEnabled &&
              <>
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', marginLeft: '60px' }} />
                {/* Duration */}
                <div className="flex items-center gap-3 px-4 py-4">
                  <span className="text-2xl">⏱️</span>
                  <span className="flex-1 text-white font-medium text-base">{ui.duration ?? 'Dauer'}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setDurationMinutes(Math.max(1, durationMinutes - 1))}
                      className="w-7 h-7 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
                      <span className="text-white font-bold text-sm">−</span>
                    </button>
                    <span className="text-white/60 text-sm">{durationMinutes} {ui.minutes ?? 'Minuten'}</span>
                    <button
                      onClick={() => setDurationMinutes(Math.min(30, durationMinutes + 1))}
                      className="w-7 h-7 rounded-full flex items-center justify-center bg-white/10 active:scale-90 transition-all">
                      <span className="text-white font-bold text-sm">+</span>
                    </button>
                  </div>
                  <ChevronRight size={16} className="text-white/40 ml-1" />
                </div>
              </>
            }
          </div>

          {/* Description */}
          <p className="text-center text-white/70 text-sm px-4 py-2 leading-relaxed">
            {ui.setupRules ?? 'Legt die Spielregeln fest. Danach gebt ihr eure Namen ein — und das Spiel beginnt!'}
          </p>
        </div>

        {/* Next Button → Player Names */}
        <div className="px-4 pb-6 pt-2 flex-shrink-0">
          <button
            onClick={handleSettingsNext}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-lg transition-all active:scale-95 hover:opacity-90"
            style={{ background: '#1C1C1E', color: '#fff' }}>
            {ui.continueNames ?? 'Weiter — Namen eingeben'}
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
